import { getLastKnownAuthUser } from "./authService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

type OperationalKind = "storage_warning" | "recognition_fallback";

type OperationalNoticePayload = {
  kind: OperationalKind;
  title: string;
  detail: string;
  event_key: string;
};

type PendingOperationalNotice = OperationalNoticePayload & {
  subject_user_id: number;
  subject_username: string;
};

const PENDING_KEY = "materialentnahme:operational-admin-history:pending:v2";
const LAST_KEY = "materialentnahme:operational-admin-history:last:v2";

function currentIdentity(): { id: number; username: string } | null {
  const user = getLastKnownAuthUser();
  return user && Number.isFinite(user.id) ? { id: user.id, username: user.username } : null;
}

function readPending(): PendingOperationalNotice[] {
  try {
    const value = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((item): item is PendingOperationalNotice => Boolean(
      item && typeof item === "object"
      && typeof item.kind === "string"
      && typeof item.title === "string"
      && typeof item.detail === "string"
      && typeof item.event_key === "string"
      && typeof item.subject_user_id === "number"
      && typeof item.subject_username === "string",
    )) : [];
  } catch { return []; }
}

function writePending(items: PendingOperationalNotice[]): void {
  // Append-only Audit darf Offline-Occurrences nicht wegen einer künstlichen
  // Queue-Obergrenze verwerfen. Die fachliche Zustands-Deduplizierung geschieht
  // vor dem Enqueue; echte neue Occurrences bleiben bis zur Übertragung erhalten.
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(items)); } catch { /* optional */ }
}

function stateKey(userId: number, kind: OperationalKind): string {
  return `${userId}:${kind}`;
}

function lastKey(userId: number, kind: OperationalKind): string | null {
  try {
    const value = JSON.parse(localStorage.getItem(LAST_KEY) ?? "{}") as Record<string, string>;
    return typeof value[stateKey(userId, kind)] === "string" ? value[stateKey(userId, kind)] : null;
  } catch { return null; }
}

function rememberLast(userId: number, kind: OperationalKind, eventKey: string): void {
  try {
    const value = JSON.parse(localStorage.getItem(LAST_KEY) ?? "{}") as Record<string, string>;
    value[stateKey(userId, kind)] = eventKey;
    localStorage.setItem(LAST_KEY, JSON.stringify(value));
  } catch { /* optional */ }
}

async function send(item: PendingOperationalNotice): Promise<"sent" | "defer" | "failed"> {
  const identity = currentIdentity();
  // Offline-Ereignisse dürfen nach einem Accountwechsel niemals dem aktuell
  // angemeldeten falschen Benutzer zugeschrieben werden. Sie bleiben solange
  // in der lokalen Queue, bis derselbe Benutzer wieder aktiv ist.
  if (!identity || identity.id !== item.subject_user_id) return "defer";
  try {
    const response = await fetchWithTimeout("/api/notices/operational-admin-history", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind:item.kind, title:item.title, detail:item.detail, event_key:item.event_key }),
    }, 3000);
    return response.ok ? "sent" : "failed";
  } catch { return "failed"; }
}

export async function recordOperationalAdminHistory(item: OperationalNoticePayload): Promise<void> {
  const identity = currentIdentity();
  if (!identity) return;
  if (lastKey(identity.id, item.kind) === item.event_key) return;
  const pending = readPending();
  if (pending.some((entry) => entry.subject_user_id === identity.id && entry.kind === item.kind && entry.event_key === item.event_key)) return;
  const pendingItem: PendingOperationalNotice = { ...item, subject_user_id:identity.id, subject_username:identity.username };
  if (await send(pendingItem) === "sent") {
    rememberLast(identity.id, item.kind, item.event_key);
    return;
  }
  writePending([...pending, pendingItem]);
}

export async function flushPendingOperationalAdminHistory(): Promise<void> {
  const pending = readPending();
  if (!pending.length) return;
  const remaining: PendingOperationalNotice[] = [];
  for (const item of pending) {
    const result = await send(item);
    if (result === "sent") rememberLast(item.subject_user_id, item.kind, item.event_key);
    else remaining.push(item);
  }
  writePending(remaining);
}

export function clearOperationalAdminHistoryState(kind: OperationalKind): void {
  const identity = currentIdentity();
  if (!identity) return;
  try {
    const value = JSON.parse(localStorage.getItem(LAST_KEY) ?? "{}") as Record<string, string>;
    delete value[stateKey(identity.id, kind)];
    localStorage.setItem(LAST_KEY, JSON.stringify(value));
  } catch { /* optional */ }
}
